import React, { useRef, useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import Form_Header from "../../../common/form-header/Form_Header";
import Common_Btn from "../../../common/common-btn/Common_Btn";
import White_Btn from "../../../common/common-btn/white-btn/White_Btn";
import Preview_Modal from "../../../common/common-modal/preview-modal/Preview_Modal";
import { useRegistration } from "../../../../context/RegistrationContext";
import { submitStep7, getRegistrationProgress } from "../../../../utils/gymOwnerApi";
import { useNavigate } from "react-router-dom";
import "./Create_Step7.css";

const Create_Step7 = ({ step, totalSteps, setStep }) => {
  const navigate = useNavigate();
  const { userId, setRegistrationData, clearRegistration } = useRegistration();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const singleFileRef = useRef(null);
  const [singleFile, setSingleFile] = useState(null);
  const [signatureError, setSignatureError] = useState("");

  const [modalShow, setModalShow] = useState(false);
  const [modalFile, setModalFile] = useState(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    register,
  } = useForm({
    defaultValues: {
      declaration_date: "",
      declaration_place: "",
      agreed_to_terms: false,
    },
  });

  useEffect(() => {
    const loadProgress = async () => {
      if (!userId) return;
      setLoadingData(true);
      try {
        const response = await getRegistrationProgress();
        if (response.success && response.data?.declaration) {
          const declaration = response.data.declaration;
          const imgUrl = process.env.REACT_APP_IMG_URL || "";

          if (declaration.declaration_date) {
            const date = new Date(declaration.declaration_date);
            setValue("declaration_date", date.toISOString().split("T")[0]);
          }
          setValue("declaration_place", declaration.declaration_place || "");

          if (declaration.digital_sign_photo) {
            setSingleFile({ preview: `${imgUrl}${declaration.digital_sign_photo}` });
          }

          setRegistrationData(response.data);
        }
      } catch (error) {
        console.error("Error loading progress:", error);
      } finally {
        setLoadingData(false);
      }
    };
    loadProgress();
  }, [userId, setValue, setRegistrationData]);

  const handleSingleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSingleFile({ file, name: file.name, preview: URL.createObjectURL(file) });
      setSignatureError("");
    }
  };

  const handleSingleButtonClick = () => {
    if (singleFile?.preview) {
      setModalFile(singleFile);
      setModalShow(true);
    } else {
      singleFileRef.current.click();
    }
  };

  const onSubmit = async (data) => {
    if (!singleFile) {
      setSignatureError("Digital signature is required");
      return;
    }

    if (!userId) {
      alert("Please complete previous steps first");
      setStep(1);
      return;
    }
    console.log({ data });

    const today = new Date().toISOString().split("T")[0];
    const [year, month, day] = today.split("-");

    const formattedDate = `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;

    setLoading(true);
    try {
      const step7Data = {
        user_id: userId,
        declaration_date: formattedDate,
        declaration_place: data.declaration_place,
        digital_sign_photo: singleFile?.file || null,
      };
      await submitStep7(step7Data);
      clearRegistration();
      setStep(8);
    } catch (error) {
      console.error("Error submitting step 7:", error);
    } finally {
      setLoading(false);
    }
  };
  console.log({ errors });

  return (
    <>
      <section className="step7_wrapper step3_wrapper step1_wrapper step4_wrapper">
        <Form_Header
          title="Declaration and Authorization"
          subtitle=""
          step={7}
          totalSteps={7}
        />

        <div className="container mt-5">
          {loadingData ? (
            <div className="text-center py-5">
              <p>Loading saved data...</p>
            </div>
          ) : (
            <>
              <div className="declaration_text_holder">
                <h5 className="top_title">You are almost there...</h5>
                <h4 className="top_subtitle">Declaration and Authorization</h4>
                <p className="declaration_text">
                  I, hereby confirm that all the information provided in this
                  registration form is true and correct, & the attached documents
                  are authentic and valid under Indian legal protocols. I am duly
                  authorized to enter into this Tie-Up Agreement with GYMIFY on
                  behalf of the above-named business entity.
                </p>
                <p className="declaration_text">
                  I understand that GYMIFY reserves the right to verify all
                  documents and information before approving the registration.
                </p>
              </div>

              <Form className="auth_form" onSubmit={handleSubmit(onSubmit)}>
                <div className="row align-items-start">

                  {/* Declaration Date */}
                  <div className="col-lg-6 mb-4">
                    <Form.Group>
                      <Form.Label>
                        Date of Declaration <span className="text-danger">*</span>
                      </Form.Label>

                      <Controller
                        name="declaration_date"
                        control={control}
                        defaultValue={new Date().toISOString().split("T")[0]}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            type="date"
                            value={new Date().toISOString().split("T")[0]}
                            disabled
                            isInvalid={!!errors.declaration_date}
                          />
                        )}
                      />

                      {errors.declaration_date && (
                        <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
                          {errors.declaration_date.message}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </div>

                  {/* Place */}
                  <div className="col-lg-6 mb-4">
                    <Form.Group>
                      <Form.Label>
                        Place <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter Place"
                        isInvalid={!!errors.declaration_place}
                        {...register("declaration_place", {
                          required: "Place is required",
                          minLength: { value: 2, message: "Place must be at least 2 characters" },
                          maxLength: { value: 100, message: "Place cannot exceed 100 characters" },
                        })}
                      />
                      {errors.declaration_place && (
                        <Form.Control.Feedback type="invalid">
                          {errors.declaration_place.message}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </div>

                  {/* Digital Signature */}
                  <div className="col-lg-6 mb-4">
                    <Form.Group>
                      <Form.Label>
                        Digital Signature <span className="text-danger">*</span>
                      </Form.Label>
                      <div
                        className={`file_upload_wrapper ${signatureError ? "border-danger" : ""}`}
                      >
                        <span className="file_placeholder">
                          {singleFile?.name || "No File Uploaded"}
                        </span>
                        <div className="upload_btn" onClick={handleSingleButtonClick}>
                          <Common_Btn btnText={singleFile ? "Preview" : "Upload file"} />
                        </div>
                        <input
                          ref={singleFileRef}
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={handleSingleFileChange}
                        />
                      </div>
                      {signatureError && (
                        <div className="text-danger mt-1" style={{ fontSize: "0.875em" }}>
                          {signatureError}
                        </div>
                      )}
                    </Form.Group>
                  </div>

                  {/* Terms & Conditions */}
                  <div className="col-lg-12 mb-4">
                    <Form.Check
                      type="checkbox"
                      id="agreed_to_terms"
                      className="label_form"
                      isInvalid={!!errors.agreed_to_terms}
                      label="By continuing, I agree to the Terms & Conditions & Privacy Policy"
                      {...register("agreed_to_terms", {
                        required: "You must agree to the Terms & Conditions & Privacy Policy",
                      })}
                    />
                    {errors.agreed_to_terms && (
                      <div className="text-danger mt-1" style={{ fontSize: "0.875em" }}>
                        {errors.agreed_to_terms.message}
                      </div>
                    )}
                  </div>

                </div>

                <div className="form_btn_holder">
                  <White_Btn btnText="Back" onClick={() => setStep(6)} />
                  <Common_Btn
                    btnText={loading ? "Submitting..." : "Register"}
                    type="submit"
                    disabled={loading}
                  />
                </div>
              </Form>
            </>
          )}
        </div>
      </section>

      {/* Preview Modal */}
      <Preview_Modal
        show={modalShow}
        onHide={() => setModalShow(false)}
        file={modalFile}
        onUploadNew={() => {
          singleFileRef.current.click();
          setModalShow(false);
        }}
        onDelete={() => {
          setSingleFile(null);
          setModalShow(false);
        }}
      />
    </>
  );
};

export default Create_Step7;